'use client'

import { useCallback, useEffect } from 'react'
import { Copy, Gift, Link2, Sparkles, UserPlus } from 'lucide-react'
import { GeneralModal, IconBox } from '@/components/ui/general-modal'
import { useReferralLink } from '@/features/referral/use-referral-link'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'

export interface IReferralModalProps {
    open: boolean
    onOpenChange: (open: boolean) => void
}

const STEPS = [
    { icon: Link2, text: 'Share your unique referral link' },
    { icon: UserPlus, text: 'They subscribe to any plan' },
    { icon: Sparkles, text: 'You earn 3,000 credits' },
]

export function ReferralModal({ open, onOpenChange }: IReferralModalProps) {
    const { referralLink, copyReferralLink, ensureReferralCode, isGenerating } = useReferralLink()

    useEffect(() => {
        if (open && !referralLink) {
            ensureReferralCode()
        }
    }, [open]) // eslint-disable-line react-hooks/exhaustive-deps

    const handleCopy = useCallback(() => {
        copyReferralLink('settings')
    }, [copyReferralLink])

    return (
        <GeneralModal
            open={open}
            onOpenChange={onOpenChange}
            title="Refer a friend, earn 3,000 credits"
            subtitle="Share your referral link with others. Once they start a Superscale subscription on any plan, you will be credited 3,000 credits. There are no limits on how many referrals you can make."
            icon={
                <IconBox>
                    <Gift className="h-5 w-5 text-[#8B6914]" />
                </IconBox>
            }
            className="md:w-[520px]"
        >
            <div className="flex flex-col gap-6">
                <div className="flex items-center gap-2">
                    <Input
                        value={referralLink ?? 'Generating link…'}
                        readOnly
                        className="font-mono text-sm flex-1 h-10"
                    />
                    <Button
                        variant="ds2-primary"
                        size="ds2"
                        onClick={handleCopy}
                        isLoading={isGenerating}
                        className="gap-2 shrink-0"
                    >
                        <Copy className="w-4 h-4" />
                        Copy
                    </Button>
                </div>

                <div className="grid grid-cols-3 gap-2.5">
                    {STEPS.map(({ icon: Icon, text }, i) => (
                        <div
                            key={i}
                            className="flex flex-col items-center text-center gap-2.5 rounded-[12px] border border-black/6 bg-[#FAFAFA] px-3 py-4"
                        >
                            <div className="flex h-8 w-8 items-center justify-center rounded-[8px] bg-white border border-black/8 shadow-[0_1px_2px_rgba(0,0,0,0.05)]">
                                <Icon className="h-3.5 w-3.5 text-[#6B7280]" />
                            </div>
                            <p className="text-[11px] leading-[1.4] text-[#6B7280]">{text}</p>
                        </div>
                    ))}
                </div>
            </div>
        </GeneralModal>
    )
}
